import { useState, useEffect, useRef, useCallback } from 'react';

interface VoiceSearchOptions {
  onResult: (transcript: string) => void;
  onError?: (error: string) => void;
  language?: string;
  continuous?: boolean;
  interimResults?: boolean;
}

interface VoiceSearchState {
  isListening: boolean;
  isSupported: boolean;
  transcript: string;
  interimTranscript: string;
  error: string | null;
}

export const useVoiceSearch = (options: VoiceSearchOptions) => {
  const {
    onResult,
    onError,
    language = 'en-US',
    continuous = false,
    interimResults = true
  } = options;

  const [state, setState] = useState<VoiceSearchState>({
    isListening: false,
    isSupported: false,
    transcript: '',
    interimTranscript: '',
    error: null
  });

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check for browser support
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      setState(prev => ({ ...prev, isSupported: true }));
      
      const recognition = new SpeechRecognition();
      recognition.continuous = continuous;
      recognition.interimResults = interimResults;
      recognition.lang = language;
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        setState(prev => ({ ...prev, isListening: true, error: null }));
      };

      recognition.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        setState(prev => ({
          ...prev,
          transcript: finalTranscript,
          interimTranscript: interimTranscript
        }));

        if (finalTranscript) {
          onResult(finalTranscript.trim());
        }
      };

      recognition.onerror = (event) => {
        const errorMessage = `Speech recognition error: ${event.error}`;
        setState(prev => ({ 
          ...prev, 
          error: errorMessage, 
          isListening: false 
        }));
        onError?.(errorMessage);
      };

      recognition.onend = () => {
        setState(prev => ({ ...prev, isListening: false }));
      };

      recognitionRef.current = recognition;
    } else {
      setState(prev => ({ ...prev, isSupported: false }));
      onError?.('Speech recognition not supported in this browser');
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [language, continuous, interimResults, onResult, onError]);

  const stopListening = useCallback(() => {
    if (!recognitionRef.current || !state.isListening) return;

    recognitionRef.current.stop();
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [state.isListening]);

  const startListening = useCallback(() => {
    if (!recognitionRef.current || state.isListening) return;

    try {
      recognitionRef.current.start();
      
      // Auto-stop after 10 seconds for better UX
      timeoutRef.current = setTimeout(() => {
        stopListening();
      }, 10000);
    } catch (error) {
      const errorMessage = 'Failed to start speech recognition';
      setState(prev => ({ ...prev, error: errorMessage }));
      onError?.(errorMessage);
    }
  }, [state.isListening, onError, stopListening]);

  const resetTranscript = useCallback(() => {
    setState(prev => ({
      ...prev,
      transcript: '',
      interimTranscript: '',
      error: null
    }));
  }, []);

  return {
    ...state,
    startListening,
    stopListening,
    resetTranscript
  };
};

// Voice commands processor
export const useVoiceCommands = () => {
  const processVoiceCommand = useCallback((transcript: string): {
    action: string;
    query?: string;
    category?: string;
  } | null => {
    const lowerTranscript = transcript.toLowerCase().trim();

    // Menu navigation commands
    if (lowerTranscript.includes('show menu') || lowerTranscript.includes('open menu')) {
      return { action: 'show_menu' };
    }

    if (lowerTranscript.includes('go to') || lowerTranscript.includes('navigate to')) {
      if (lowerTranscript.includes('home')) return { action: 'navigate', query: 'home' };
      if (lowerTranscript.includes('about')) return { action: 'navigate', query: 'about' };
      if (lowerTranscript.includes('contact')) return { action: 'navigate', query: 'contact' };
    }

    // Search commands
    if (lowerTranscript.includes('search for') || lowerTranscript.includes('find')) {
      const searchTerms = lowerTranscript
        .replace(/search for|find|show me/g, '')
        .trim();
      return { action: 'search', query: searchTerms };
    }

    // Category filtering
    if (lowerTranscript.includes('show') && lowerTranscript.includes('burger')) {
      return { action: 'filter', category: 'burgers' };
    }
    if (lowerTranscript.includes('show') && lowerTranscript.includes('chicken')) {
      return { action: 'filter', category: 'chicken' };
    }
    if (lowerTranscript.includes('show') && lowerTranscript.includes('salad')) {
      return { action: 'filter', category: 'salads' };
    }

    // Order commands
    if (lowerTranscript.includes('order') || lowerTranscript.includes('add to cart')) {
      const itemName = lowerTranscript
        .replace(/order|add to cart|i want|get me/g, '')
        .trim();
      return { action: 'order', query: itemName };
    }

    // Clear/reset commands
    if (lowerTranscript.includes('clear') || lowerTranscript.includes('reset')) {
      return { action: 'clear' };
    }

    return null;
  }, []);

  return { processVoiceCommand };
}; 